import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Archive, BookHeart } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { WishlistCard } from "@/components/WishlistCard";
import { WishlistDialog } from "@/components/WishlistDialog";

export default function WishlistsPage() {
  const { t } = useLang();
  const [wishlists, setWishlists] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/wishlists");
      setWishlists(res.data);
    } catch (err) {
      console.error("wishlists load failed", err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const active = wishlists?.filter((w) => !w.is_archived) || [];
  const archived = wishlists?.filter((w) => w.is_archived) || [];

  return (
    <div data-testid="wishlists-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold">{t("my_wishlists")}</h1>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" asChild data-testid="wishlists-memories-link">
            <Link to="/anilar"><BookHeart size={15} className="mr-1" /> {t("memories_link")}</Link>
          </Button>
          <Button onClick={() => setDialogOpen(true)} className="rounded-full active:scale-95 transition-transform" data-testid="wishlists-new-btn">
            <Plus size={16} className="mr-1" /> {t("new_list")}
          </Button>
        </div>
      </div>
      {!wishlists ? (
        <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : active.length === 0 && archived.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center">
          <p className="text-4xl mb-3">🎁</p>
          <p className="text-muted-foreground mb-4">{t("no_lists_long")}</p>
          <Button onClick={() => setDialogOpen(true)} className="rounded-full" data-testid="wishlists-empty-create-btn">{t("create_first")}</Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {active.map((wl, i) => <WishlistCard key={wl.wishlist_id} wishlist={wl} delay={i * 0.05} />)}
          </div>
          {archived.length > 0 && (
            <section className="mt-10" data-testid="archived-section">
              <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2 text-muted-foreground">
                <Archive size={17} /> {t("archive_section")}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 opacity-75">
                {archived.map((wl, i) => <WishlistCard key={wl.wishlist_id} wishlist={wl} delay={i * 0.05} />)}
              </div>
            </section>
          )}
        </>
      )}
      <WishlistDialog open={dialogOpen} onOpenChange={setDialogOpen} onSaved={load} />
    </div>
  );
}
